import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, Divider, Popover, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
// import { capitalize } from 'lodash';
import { generateEvaluationPdf } from './utils/generatePdfReport';
import { useSession } from 'components/Providers';
import { PeopleAltTwoTone, VideoCameraFrontTwoTone, OpenInNewTwoTone } from '@mui/icons-material';
import { useState } from 'react';
import { save as saveEvaluation } from 'store/evaluation/admin-eval-self'
import { useDispatch } from 'react-redux';
// import jsPDF from 'jspdf';

const EvaluationDetails = ({ evaluationDetail, evaluationReport }) => {
    const { t } = useTranslation();
    const { person, academicYear } = useSession();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [powerPointLink, setPowerPointLink] = useState('');
    const [visitLinkKey, setVisitLinkKey] = useState(null);
    const dispatch = useDispatch();

    const boxStyles = {
        bgcolor: "background.paper",
        p: 2,
        borderRadius: 2.5,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        overflowX: "auto",
    };

    const savePowerPointLink = async (powerPointLink, visitLinkKey) => {
        const payload = {
            [visitLinkKey]: powerPointLink,
        };

        try {
            await dispatch(saveEvaluation(payload));
        } catch (error) {
            console.error('Error saving PowerPoint Link:', error);
        }

        onClosePopover();
    };

    const onClosePopover = () => {
        setAnchorEl(null);
        setPowerPointLink('');
    };

    const renderEvaluationSection = (title, date, type, visitLinkKey) => (        
        <Stack direction="column" spacing={0}>
            <Typography variant="body2" color="textSecondary">
                {t(title)}
            </Typography>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <Chip
                    label={date ? dayjs(date).format('MM/DD/YYYY h:mm A') : 'Not Scheduled'}
                    color={date ? 'primary' : 'default'}
                    size='small'
                />
                {type && (
                    <Tooltip title={type === 'in-person' ? 'In Person Meeting' : 'Virtual Meeting'} arrow>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'default', pointerEvents: 'auto' }}>
                            {type === 'in-person' ? <PeopleAltTwoTone color="secondary" /> : <VideoCameraFrontTwoTone color='secondary' />}
                        </Box>
                    </Tooltip>
                )}
                {evaluationDetail?.[visitLinkKey] && (
                    <Tooltip title={t('Open PowerPoint File')} arrow>
                        <Box
                            sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', pointerEvents: 'auto' }}
                            onClick={() => window.open(evaluationDetail[visitLinkKey], '_blank')}
                        >
                            <OpenInNewTwoTone color='secondary' />
                        </Box>
                    </Tooltip>
                )}
            </Box>
            <Box>
                <Button
                    variant="text"
                    size="small"
                    onClick={(event) => {
                        setAnchorEl(event.currentTarget);
                        setVisitLinkKey(visitLinkKey);
                        setPowerPointLink(evaluationDetail?.[visitLinkKey] || '');
                      }}
                    sx={{ textTransform: 'none' }}
                >
                    { evaluationDetail?.[visitLinkKey] ? t('Update PowerPoint Link') : t('Add PowerPoint Link')}
                </Button>
            </Box>
        </Stack>
    );

    const openEvaluationDialog = () => {
        setDialogOpen(true);
    };

    const closeEvaluationDialog = () => {
        setDialogOpen(false);
    };

    const EvaluationDialog = () => (
        <Dialog open={dialogOpen} onClose={closeEvaluationDialog}>
            <DialogTitle>{t('Evaluation Feedback')}</DialogTitle>
            <DialogContent>
                <Typography variant="body1" color="textPrimary">
                    {evaluationDetail?.feedback || t('Not Available Yet')}
                </Typography>
            </DialogContent>
        </Dialog>
    );

    return (
        <Box sx={{ ...boxStyles, flexGrow: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{t('Evaluation Details')}</Typography>
                <Button variant="contained" color="primary" size="small" onClick={() => { generateEvaluationPdf(evaluationReport, person, academicYear) }}>
                    {t('Evaluation Report')}
                </Button>
            </Box>

            <Box display={"flex"} flexDirection={{ xs: 'column', sm: 'row' }} p={2} gap={{ xs: 1, sm: 2, md: 3, lg: 4, xl: 5 }} flexWrap="wrap" justifyContent={'flex-start'} >
                {renderEvaluationSection('Site Visit 1', evaluationDetail?.evaluation1Date, evaluationDetail?.evaluation1Type, 'visit1Link')}
                <Divider orientation="vertical" flexItem sx={{ mx: 3, display: { xs: 'none', sm: 'block' } }} />
                {renderEvaluationSection('Site Visit 2', evaluationDetail?.evaluation2Date, evaluationDetail?.evaluation2Type, 'visit2Link')}
                <Divider orientation="vertical" flexItem sx={{ mx: 3, display: { xs: 'none', sm: 'block' } }} />
                {renderEvaluationSection('Summative Visit', evaluationDetail?.summativeDate, evaluationDetail?.summativeType, 'summativeLink')}
                <Divider orientation="vertical" flexItem sx={{ mx: 3, display: { xs: 'none', sm: 'block' } }} />
                <Stack direction="column" spacing={0}>
                    <Typography variant="body2" color="textSecondary">
                        {t('Feedback')}
                    </Typography>
                    <Chip
                        label={evaluationDetail?.feedback ? t('View Details') : t('Not Available Yet')}
                        color={evaluationDetail?.feedback ? 'primary' : 'default'}
                        onClick={openEvaluationDialog}
                        size='small'
                        sx={{ cursor: 'pointer', width: 'fit-content' }}
                    />
                </Stack>
                <Divider orientation="vertical" flexItem sx={{ mx: 3, display: { xs: 'none', sm: 'block' } }} />
                <Stack direction="column" spacing={0}>
                    <Typography variant="body2" color="textSecondary">
                        {t('Status')}
                    </Typography>
                    <Chip
                        label={evaluationDetail?.isFinalized ? t('Finalized') : t('Not Finalized')}
                        color={evaluationDetail?.isFinalized === true ? 'success' : 'default'}
                        size='small'
                        sx={{ width: 'fit-content' }}
                    />
                </Stack>
            </Box>

            <EvaluationDialog />

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={onClosePopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Box sx={{ p: 2, minWidth: 300 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        {evaluationDetail?.[visitLinkKey] ? t('Update PowerPoint Link') : t('Add PowerPoint Link')}
                    </Typography>
                    <TextField
                        label={t('PowerPoint Link')}
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={powerPointLink}
                        onChange={(e) => setPowerPointLink(e.target.value)}
                    />
                    <Box display="flex" justifyContent="flex-end">
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{ mt: 1, }}
                            onClick={() => {
                                if (visitLinkKey) {
                                    savePowerPointLink( powerPointLink, visitLinkKey);
                                }
                            }}
                        >
                            {t('Save')}
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </Box>
    );
}

export default EvaluationDetails